import { Injectable, NgZone, OnDestroy, Signal, computed, inject, signal } from '@angular/core';

import { Observable, Subject, Subscriber, Subscription } from 'rxjs';

import { OutageScreenComponent } from '@common/component/outage-screen/outage-screen.component';
import { OutageUncertainPromptComponent } from '@common/component/outage-screen/outage-uncertain-prompt.component';
import { GlobalNotificationService } from '@common/service/global-notification.service';
import { SystemNotificationService } from '@common/service/system-notification.service';
import { SystemNotificationRef } from '@common/types/system-notification.interface';
import {
  DeploymentReason,
  UNKNOWN_DEPLOYMENT_REASON,
  deploymentReason,
  readDeploymentStatus,
} from '@common/util/deployment-status.util';
import {
  OUTAGE_COPY,
  OUTAGE_ESCAPE_MS,
  OUTAGE_OK_STREAK,
  OUTAGE_SLOW_MS,
  OUTAGE_SLOW_POLL_MS,
  pingPlatform,
  pollDelay,
} from '@common/util/outage.util';
import { environment } from '@environments/environment';

export type OutageState = 'idle' | 'outage' | 'dismissed';
export type OutageReportSource = 'http' | 'socket';
/** initial = no buttons (0-30 s); escape = secondary exits (30 s+); slow = + warning copy and Check now (1 min+). */
export type OutageStage = 'initial' | 'escape' | 'slow';
/** `upgrade` = a held error carried the deploy marker (dormant until a backend sends it). */
export type OutageVariant = 'outage' | 'upgrade';
export type HeldKind = 'replay' | 'uncertain';
/** Result feedback after a user-pressed Check now, shown in the status row. */
export type CheckFeedback = 'none' | 'unreachable' | 'back';

/** A manual Check now shows "Checking…" for at least this long. */
export const OUTAGE_CHECK_MIN_MS = 1000;
/** How long "Still unreachable" stays in the status row. */
export const OUTAGE_FEEDBACK_MS = 4000;
/** How long the button stays disabled after a failed manual check (the screen drains a fill over this time). */
export const OUTAGE_COOLDOWN_MS = 10_000;

interface HeldCall {
  label: string;
  kind: HeldKind;
  replay: () => Observable<unknown>;
  subscriber: Subscriber<unknown>;
  error: unknown;
  inner?: Subscription;
  cancelled?: boolean;
}

/**
 * AIWB-1175 — platform outage hold / wait / resume, at load time and at run time.
 * Spec: design/auth/2026-10-06-aiwb1175-outage-hold-replay-spec.md §4.0–4.4.
 */
@Injectable({ providedIn: 'root' })
export class OutageService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly systemNotification = inject(SystemNotificationService);
  private readonly globalNotification = inject(GlobalNotificationService);

  private readonly _state = signal<OutageState>('idle');
  readonly state: Signal<OutageState> = this._state.asReadonly();
  private readonly _recovered = new Subject<void>();
  readonly recovered$ = this._recovered.asObservable();

  private readonly _stage = signal<OutageStage>('initial');
  readonly stage: Signal<OutageStage> = this._stage.asReadonly();
  private readonly _variant = signal<OutageVariant>('outage');
  readonly variant: Signal<OutageVariant> = this._variant.asReadonly();
  private readonly _booted = signal(false);
  readonly booted: Signal<boolean> = this._booted.asReadonly();
  /** A manual Check now is in flight (the button shows "Checking…" and is disabled). */
  private readonly _checking = signal(false);
  readonly checking: Signal<boolean> = this._checking.asReadonly();
  private readonly _feedback = signal<CheckFeedback>('none');
  readonly feedback: Signal<CheckFeedback> = this._feedback.asReadonly();
  /** The post-failure cooldown is running (the button is disabled and its fill drains). */
  private readonly _cooling = signal(false);
  readonly cooling: Signal<boolean> = this._cooling.asReadonly();
  /** The Check now button is not pressable: in flight, cooling down, or awaiting the confirming check. */
  readonly checkLocked = computed(() => this._checking() || this._cooling() || this._feedback() === 'back');

  protected readonly deploymentEnvironment = environment.deploymentStatusEnvironment;
  readonly reasonEnabled = !!this.deploymentEnvironment;
  private readonly _reason = signal<DeploymentReason>(UNKNOWN_DEPLOYMENT_REASON);
  readonly reason = this._reason.asReadonly();
  private reasonRequest: AbortController | null = null;
  private reasonTimeout: ReturnType<typeof setTimeout> | null = null;
  private reasonExpiry: ReturnType<typeof setTimeout> | null = null;

  protected readDeploymentStatus(signal: AbortSignal): Promise<unknown> {
    return readDeploymentStatus(signal);
  }

  private clearReason(): void {
    this.reasonRequest?.abort();
    this.reasonRequest = null;
    if (this.reasonTimeout) clearTimeout(this.reasonTimeout);
    if (this.reasonExpiry) clearTimeout(this.reasonExpiry);
    this.reasonTimeout = this.reasonExpiry = null;
    this._reason.set(UNKNOWN_DEPLOYMENT_REASON);
  }

  private refreshReason(): void {
    if (!this.deploymentEnvironment || this._state() !== 'outage' || this.reasonRequest) return;
    const request = new AbortController();
    this.reasonRequest = request;
    this.reasonTimeout = setTimeout(() => this.clearReason(), 5000);
    void this.readDeploymentStatus(request.signal)
      .catch(() => null)
      .then((record) => {
        // A dismissed/recovered outage or a timed-out request must not update a new screen.
        if (request !== this.reasonRequest || request.signal.aborted) return;
        if (this.reasonTimeout) clearTimeout(this.reasonTimeout);
        if (this.reasonExpiry) clearTimeout(this.reasonExpiry);
        this.reasonTimeout = this.reasonExpiry = null;
        this.reasonRequest = null;
        const reason = deploymentReason(record, this.deploymentEnvironment!);
        this._reason.set(reason);
        if (reason.expiresAt) {
          this.reasonExpiry = setTimeout(
            () => {
              this._reason.set(UNKNOWN_DEPLOYMENT_REASON);
              this.reasonExpiry = null;
            },
            Math.max(0, reason.expiresAt - Date.now()),
          );
        }
      });
  }

  private queue: HeldCall[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private okStreak = 0;
  private startedAt = 0;
  private stageTimers: ReturnType<typeof setTimeout>[] = [];
  private inflight: Promise<void> | null = null;
  private lastPingOk = false;
  /** Invalidates an in-flight manual press when the outage ends or restarts. */
  private pressId = 0;
  private cooldownTimer: ReturnType<typeof setTimeout> | null = null;
  private feedbackTimer: ReturnType<typeof setTimeout> | null = null;
  private screen: SystemNotificationRef | null = null;
  private promptRef: SystemNotificationRef | null = null;
  private promptEntries: HeldCall[] = [];
  private readonly onVisibility = () => {
    if (this._state() !== 'idle' && !document.hidden) {
      this.zone.run(() => this.pollNow());
    }
  };

  constructor() {
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  ngOnDestroy(): void {
    this.clearReason();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.clearTimer();
    this.clearStageTimers();
    this.resetCheckUi();
  }

  get active(): boolean {
    return this._state() === 'outage';
  }

  get dismissed(): boolean {
    return this._state() === 'dismissed';
  }

  markBooted(): void {
    this._booted.set(true);
  }

  confirmDown(): Promise<boolean> {
    return this.ping().then((ok) => !ok);
  }

  /** Hold `replay` until the platform is back. Unsubscribing drops the call (it is never replayed). */
  hold<T>(
    label: string,
    replay: () => Observable<T>,
    originalError: unknown,
    kind: HeldKind = 'replay',
    deployMarker = false,
  ): Observable<T> {
    return new Observable<T>((subscriber) => {
      if (this._state() === 'dismissed') {
        // The user chose Stop waiting: no re-trap until the platform is back.
        subscriber.error(originalError);
        return undefined;
      }
      const entry: HeldCall = {
        label,
        kind,
        replay: replay as () => Observable<unknown>,
        subscriber: subscriber as Subscriber<unknown>,
        error: originalError,
      };
      this.queue.push(entry);
      console.debug('[Outage] held', label, `(queue: ${this.queue.length})`);
      this.enter();
      if (deployMarker) {
        this._variant.set('upgrade');
      }
      return () => {
        entry.cancelled = true;
        const i = this.queue.indexOf(entry);
        if (i >= 0) {
          this.queue.splice(i, 1);
        }
        entry.inner?.unsubscribe();
      };
    });
  }

  /** A signal from outside HTTP (e.g. a dropped socket): confirm with a ping, enter the outage only if it is down. */
  report(source: OutageReportSource): void {
    if (this._state() !== 'idle') {
      return;
    }
    void this.confirmDown().then((down) => {
      if (down && this._state() === 'idle') {
        console.debug('[Outage] confirmed down via', source);
        this.zone.run(() => this.enter());
      }
    });
  }

  protected ping(): Promise<boolean> {
    return pingPlatform();
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  private enter(): void {
    if (this._state() !== 'idle') {
      return;
    }
    this.reclaimUncertainPrompt();
    console.debug('[Outage] entering outage; polling /api/ping with backoff');
    this._state.set('outage');
    this.clearReason();
    this.refreshReason();
    this.startedAt = Date.now();
    this.okStreak = 0;
    this._stage.set('initial');
    this._variant.set('outage');
    this.resetCheckUi();
    this.screen = this.systemNotification.show(
      { component: OutageScreenComponent, dismissible: false, overlayClass: 'blocking-overlay-bare' },
      { priority: 'outage' },
    );
    this.armStageTimers();
    this.schedule();
  }

  private schedule(): void {
    this.clearTimer();
    const elapsed = this._state() === 'dismissed' ? OUTAGE_SLOW_POLL_MS : Date.now() - this.startedAt;
    const delay = pollDelay(elapsed, this.okStreak);
    this.timer = this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        this.timer = null;
        if (document.hidden) {
          console.debug('[Outage] tab hidden; pausing until visible');
          return;
        }
        // The timer lives outside Angular (no change-detection churn while idle-waiting), but the
        // poll runs inside it so the "Checking..." status and escape copy render.
        this.zone.run(() => void this.poll());
      }, delay),
    );
  }

  /** At most one ping in flight: an overlapping call returns the pending one. */
  private poll(): Promise<void> {
    if (this.inflight) {
      return this.inflight;
    }
    if (this._state() === 'idle') {
      return Promise.resolve();
    }
    const run = this.runPoll().finally(() => {
      this.inflight = null;
    });
    this.inflight = run;
    return run;
  }

  private async runPoll(): Promise<void> {
    this.refreshReason();
    const ok = await this.ping();
    this.lastPingOk = ok;
    if (this._state() === 'idle') {
      return;
    }
    if (!ok && this._feedback() === 'back') {
      // The confirming check after a manual "back online" failed: fall back to the normal flow.
      this.clearFeedback();
      this.startCooldown();
    }
    this.okStreak = ok ? this.okStreak + 1 : 0;
    console.debug('[Outage] ping', ok ? 'OK' : 'down', `(streak ${this.okStreak})`);
    if (this.okStreak >= OUTAGE_OK_STREAK) {
      this.zone.run(() => this.recover());
      return;
    }
    this.schedule();
  }

  private pollNow(): void {
    this.clearTimer();
    void this.poll();
  }

  /**
   * The Check now button (user-pressed only). Disabled "Checking…" for at least OUTAGE_CHECK_MIN_MS; then a failure
   * shows "Still unreachable" and starts a cooldown, a success shows "Back online" while the two-OK rule confirms.
   */
  checkNow(): void {
    if (this.checkLocked() || this._state() !== 'outage') {
      return;
    }
    const press = ++this.pressId;
    this.clearFeedback();
    this._checking.set(true);
    this.clearTimer();
    const minDisplay = new Promise<void>((resolve) => setTimeout(resolve, OUTAGE_CHECK_MIN_MS));
    void Promise.all([this.poll(), minDisplay]).then(() => {
      if (press !== this.pressId) {
        return;
      }
      this._checking.set(false);
      if (this._state() !== 'outage') {
        return;
      }
      if (this.lastPingOk) {
        this._feedback.set('back');
        return;
      }
      this._feedback.set('unreachable');
      this.feedbackTimer = this.zone.runOutsideAngular(() =>
        setTimeout(() => this.zone.run(() => this.clearFeedback()), OUTAGE_FEEDBACK_MS),
      );
      this.startCooldown();
    });
  }

  private startCooldown(): void {
    this.stopCooldown();
    this._cooling.set(true);
    this.cooldownTimer = this.zone.runOutsideAngular(() =>
      setTimeout(() => this.zone.run(() => this.stopCooldown()), OUTAGE_COOLDOWN_MS),
    );
  }

  private stopCooldown(): void {
    if (this.cooldownTimer) {
      clearTimeout(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    this._cooling.set(false);
  }

  private clearFeedback(): void {
    if (this.feedbackTimer) {
      clearTimeout(this.feedbackTimer);
      this.feedbackTimer = null;
    }
    this._feedback.set('none');
  }

  /** Clears every Check now artefact (feedback, cooldown timer, in-flight press). */
  private resetCheckUi(): void {
    this.pressId++;
    this.stopCooldown();
    this.clearFeedback();
    this._checking.set(false);
  }

  reload(): void {
    this.reloadPage();
  }

  private armStageTimers(): void {
    this.clearStageTimers();
    const at = (ms: number, fn: () => void) =>
      this.stageTimers.push(this.zone.runOutsideAngular(() => setTimeout(() => this.zone.run(fn), ms)));
    at(OUTAGE_ESCAPE_MS, () => this.setStage('escape'));
    at(OUTAGE_SLOW_MS, () => this.setStage('slow'));
  }

  private clearStageTimers(): void {
    this.stageTimers.forEach((t) => clearTimeout(t));
    this.stageTimers = [];
  }

  private setStage(stage: OutageStage): void {
    if (this._state() !== 'outage') {
      return;
    }
    console.debug('[Outage] stage ->', stage);
    this._stage.set(stage);
  }

  /** The Stop waiting button: release held calls with their errors; no re-trap until the platform is back. */
  stopWaiting(): void {
    console.debug('[Outage] stop waiting; releasing held calls with their errors');
    this._state.set('dismissed');
    this.clearReason();
    this.okStreak = 0;
    this.clearStageTimers();
    this.resetCheckUi();
    this.screen?.close();
    this.screen = null;
    const entries = this.queue;
    this.queue = [];
    entries.forEach((entry) => entry.subscriber.error(entry.error));
    this.schedule();
  }

  private recover(): void {
    this.clearReason();
    this.clearTimer();
    this.clearStageTimers();
    this.resetCheckUi();
    const previous = this._state();
    this._state.set('idle');
    this.okStreak = 0;
    console.debug('[Outage] platform back; was', previous);
    this._recovered.next();
    if (previous === 'dismissed') {
      this.globalNotification.show({
        type: 'info',
        title: OUTAGE_COPY.backTitle,
        message: OUTAGE_COPY.backBody,
        duration: 6000,
      });
      return;
    }
    this.screen?.close();
    this.screen = null;
    const entries = this.queue;
    this.queue = [];
    entries.filter((e) => e.kind === 'replay').forEach((e) => this.replayEntry(e));
    const uncertain = entries.filter((e) => e.kind === 'uncertain');
    if (uncertain.length) {
      this.promptUncertain(uncertain);
    }
  }

  private replayEntry(entry: HeldCall): void {
    if (entry.cancelled) {
      return;
    }
    console.debug('[Outage] replaying', entry.label);
    entry.inner = entry.replay().subscribe(entry.subscriber);
  }

  /** A new outage began while the uncertain prompt was open: close it, re-queue its entries to ask again. */
  private reclaimUncertainPrompt(): void {
    if (!this.promptRef) {
      return;
    }
    const pending = this.promptEntries.filter((e) => !e.cancelled);
    this.promptRef.close();
    this.promptRef = null;
    this.promptEntries = [];
    // Original (older) calls go ahead of anything held since.
    this.queue = [...pending, ...this.queue];
  }

  private promptUncertain(entries: HeldCall[]): void {
    const settle = () => {
      ref.close();
      if (this.promptRef === ref) {
        this.promptRef = null;
        this.promptEntries = [];
      }
    };
    const ref: SystemNotificationRef = this.systemNotification.show(
      {
        component: OutageUncertainPromptComponent,
        componentData: {
          onSendAgain: () => {
            settle();
            entries.forEach((e) => this.replayEntry(e));
          },
          onDontSend: () => {
            settle();
            entries.filter((e) => !e.cancelled).forEach((e) => e.subscriber.error(e.error));
          },
        },
        dismissible: false,
        overlayClass: 'blocking-overlay-bare',
      },
      // Outage priority (not normal): a suspended session screen must not swallow this prompt.
      { priority: 'outage' },
    );
    this.promptRef = ref;
    this.promptEntries = entries;
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
